/**
 * The app's one dialog (DEC-197, UI_SDD §R.13), extracted from DEC-067's LLM decision
 * modal: a centred panel over a dimmed page, `role="dialog"` with `aria-modal`, Esc
 * closes, focus moves in on open, stays inside while open (Tab wraps) and returns to
 * what had it on close.
 *
 * Destructive confirmations stay inline (DEC-191); this is for reading, not for asking.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'

interface ModalProps {
  title: ReactNode
  onClose: () => void
  children: ReactNode
  /** A line under the title. */
  subtitle?: ReactNode
  /** A dot before the title, as a status mark (a CSS colour). */
  dot?: string
  maxWidth?: number
  testId?: string
  /** False for a decision the user must take (no close button, backdrop does nothing). */
  dismissible?: boolean
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, summary, [tabindex]:not([tabindex="-1"])'

export function Modal({
  title,
  onClose,
  children,
  subtitle,
  dot,
  maxWidth = 560,
  testId,
  dismissible = true,
}: ModalProps) {
  const titleId = useId()
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? panel.current)?.focus()
    return () => before?.focus?.()
  }, [])

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onClose()
      return
    }
    if (e.key !== 'Tab' || !panel.current) return
    const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
    if (items.length === 0) return
    const firstEl = items[0]
    const lastEl = items[items.length - 1]
    if (e.shiftKey && document.activeElement === firstEl) {
      e.preventDefault()
      lastEl.focus()
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      e.preventDefault()
      firstEl.focus()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 sm:p-6"
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
        onKeyDown={onKeyDown}
        className="bg-panel border border-hairline rounded-lg shadow-overlay w-full max-h-[calc(100vh-48px)] overflow-y-auto p-6 focus:outline-none"
        style={{ maxWidth }}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              {dot && <span aria-hidden className="w-2.5 h-2.5 rounded-full flex-none" style={{ background: dot }} />}
              <h2 id={titleId} className="font-semibold text-[16px] leading-snug text-ink m-0">
                {title}
              </h2>
            </div>
            {subtitle && <div className="text-[12.5px] leading-[1.5] text-muted mt-1">{subtitle}</div>}
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex-none rounded-md p-1 text-muted hover:text-ink hover:bg-hover-hue focus:outline-none focus-visible:shadow-focus"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  )
}
